/* ==========================================================================
   IL | Availability block
   --------------------------------------------------------------------------
   NOT part of Prestige. Loaded from layout/theme.liquid.

   Takes the button the REZ app injects and moves it into our own markup:
   the panel on a product page, the quick add corner slot on a collection card.

   MOVES, NEVER RECREATES. A copy of the button would need REZ's click handler,
   their product and variant ids, and their modal wiring reproduced here, and
   every one of those is theirs to change without telling us. Moving the real
   node keeps their handlers attached, so their flow runs exactly as it does
   today and we own only where it sits and how it looks.

   REZ injects asynchronously and re-injects when a collection page paginates,
   so this observes the DOM rather than running once on load.

   Everything degrades to nothing. If the app is off, the button never appears,
   the panel stays hidden and the card is untouched, which is the right result
   for a product with no campaign and no waitlist.

   Re-apply after a Prestige update: copy this file back and re-add the
   stylesheet and script tags in layout/theme.liquid. See reference/THEME-EDITS.md.
   ========================================================================== */
(() => {
  'use strict';

  // REZ's own class names, READ OFF THE RENDERED PAGE rather than guessed. The first
  // attempt guessed `-btn` and the app actually uses `-button`, so nothing matched and
  // the panel stayed hidden with no error anywhere. If REZ rename these, adoption stops
  // and their button simply shows where it always did, which is a visible fallback
  // rather than a broken page.
  //
  // The wrapper is what gets moved when there is one, so any layout or handler REZ
  // attached to it travels with the button instead of being left behind.
  const PDP_BUTTON = '.rez-stock-notification-button, .rez-preorder-button, .rez-button-wrapper > button';
  const CARD_BUTTON = '.rez-out-of-stock-collection-btn';

  const isVisible = (el) => !!(el && el.getBoundingClientRect().width > 0);

  const body = document.body;
  const wantBell = body.dataset.ilBell === 'true';
  const bellLabel = body.dataset.ilBellLabel || '';

  const BELL = `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
      stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.7 21a2 2 0 0 1-3.4 0"/></svg>`;

  /* ---------------------------------------------------------------- product */

  function adoptPdp(host) {

    // REZ renders more than one wrapper, only one of which is on screen, so take the
    // first that actually has a box rather than the first in document order.
    const scope = host.closest('product-rerender') || host.closest('.shopify-section');
    const button = [...(scope || host).querySelectorAll(PDP_BUTTON)].find(isVisible);
    if (!button || host.contains(button)) return;

    // With the notify drawer on, the sold out panel is already rendered and visible with our
    // own button in it. The only thing left for adoption is REZ's PRE-ORDER button, so anything
    // else is left where it is rather than pulled into a panel that no longer has a slot.
    if (document.querySelector('#il-notify-drawer') && host.querySelector('[data-il-notify]') && !/preorder/i.test(button.className +
        ' ' + (button.closest('.rez-button-wrapper') || button).className)) {
      (button.closest('.rez-button-wrapper') || button).remove();
      return;
    }

    // Which state are we in? Decided from the button REZ actually rendered, never
    // guessed in Liquid, because Liquid cannot see a campaign.
    const wrapper = button.closest('.rez-button-wrapper') || button;
    const isPreorder = /preorder/i.test(wrapper.className + ' ' + button.className);
    host.dataset.ilState = isPreorder ? 'preorder' : 'soldout';

    const panel = host.querySelector(
      isPreorder ? '.il-availability__panel--preorder' : '.il-availability__panel--soldout'
    );
    const slot = panel?.querySelector('[data-il-slot]');
    if (!slot) return;

    // Give it Prestige's own button classes so it is styled by the theme rather
    // than by the app's stylesheet, then move it in. Their classes stay on it so
    // their own script can still find it.
    //
    // The inline style goes for the same reason it goes on the card: REZ write their
    // colour and radius as an attribute, which beats any class. Left on, the adopted
    // button rendered pure black while every other primary button on the site is
    // #141414. Close enough to look deliberate, wrong enough to defeat the point of
    // adopting it. Caught by reading the computed style, not by eye.
    button.removeAttribute('style');
    button.classList.add('button', 'w-full', 'il-availability__button');
    slot.appendChild(wrapper);

    host.hidden = false;
    host.dataset.ilAdopted = 'true';
  }

  /* ------------------------------------------------------------------ cards */

  function adoptCards() {
    // The bell is off, so the merchant has chosen the app's own button. Leave it exactly as REZ
    // drew it and touch nothing. This is the switch between the two, and it is the only branch
    // where REZ's button is supposed to survive.
    if (!wantBell) return;

    // IL EDIT: with the drawer on, the bell is already rendered by snippets/product-card.liquid,
    // so REZ's button is no longer the raw material for ours. It is a SECOND notify control.
    //
    // This used to be an early return alongside the check above, which read as "the Liquid bell
    // supersedes adoption" and was right about that, but it left REZ's button on the card
    // untouched. Adoption was also what stripped REZ's inline style, so without it the button
    // renders as the full width black bar it always was underneath: 291x56 against our 37x39
    // bell, the obvious thing to click, and its signups go to REZ's waitlist rather than the
    // Klaviyo flow. It also made sold out cards 56px taller than the rest of the grid, which is
    // the exact thing the corner bell exists to avoid.
    //
    // Remove the duplicate control entirely so an app style update cannot reveal it again.
    // display:none would also hide it from keyboard and screen-reader navigation. The
    // observer below re-runs this when Prestige swaps the grid on filter or pagination and REZ
    // re-injects. Turning the app's back in stock alert off does NOT prevent this: REZ still
    // renders the button with its own config reading bis_enabled false.
    // See reference/CUTOVER.md item 30.
    document.querySelectorAll(`${CARD_BUTTON}:not([data-il-bell])`).forEach((button) => {
      const card = button.closest('.product-card');
      if (!card) return;
      if (document.querySelector('#il-notify-drawer') && card.querySelector('[data-il-notify]')) {
        button.remove();
        return;
      }

      // The corner slot. Prestige puts its quick add inside the figure, so the bell
      // lands in the identical position and the card height never changes.
      const slot = card.querySelector('.product-card__figure') || card;

      button.dataset.ilBell = 'true';
      button.textContent = '';
      button.className = 'product-card__quick-add-button il-bell';
      // REZ writes its width, height, background and radius as an INLINE style from the
      // app's design settings. Inline beats any class, so with their class swapped out
      // and their style left on, the bell rendered as a full width black bar across the
      // bottom of the image rather than a corner button. Nothing in the CSS could have
      // won that fight, so the attribute goes.
      button.removeAttribute('style');
      if (bellLabel) button.setAttribute('aria-label', bellLabel);
      button.insertAdjacentHTML('afterbegin', BELL);
      if (bellLabel) {
        const label = document.createElement('span');
        label.className = 'il-bell__label';
        label.textContent = bellLabel;
        button.appendChild(label);
      }
      slot.appendChild(button);
    });
  }

  /* --------------------------------------------------------------- observer */

  function run() {
    for (const host of document.querySelectorAll('il-availability:not([data-il-adopted])')) adoptPdp(host);
    adoptCards();
  }

  // Prestige swaps the whole collection section on filter and pagination, and REZ
  // re-injects afterwards, so one pass on load is not enough.
  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(() => { scheduled = false; run(); });
  });

  function start() {
    run();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
